import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  MenuItem,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import { RHFSelect, RHFTextField } from '../../../components/hook-form';
import FormProvider from '../../../components/hook-form/FormProvider';
import { useSnackbar } from '../../../components/snackbar';
// api
import { StockAdjustmentService } from '../../../api/stock-adjustment.service';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: VoidFunction;
  product: { id: string; name: string; currentStock: number } | null;
  onSuccess: VoidFunction;
};

type FormValues = {
  type: 'increase' | 'decrease';
  qty: number;
  reason: string;
  notes: string;
};

export default function StockAdjustmentDialog({
  open,
  onClose,
  product,
  onSuccess,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const AdjustmentSchema = Yup.object().shape({
    type: Yup.string().required('Adjustment type is required'),
    qty: Yup.number()
      .transform((value, originalValue) =>
        originalValue === '' ? undefined : value,
      )
      .required('Quantity is required')
      .min(1, 'Quantity must be at least 1'),
    reason: Yup.string().required('Reason is required'),
    notes: Yup.string(),
  });

  const defaultValues: any = {
    type: '',
    qty: '',
    reason: '',
    notes: '',
  };

  const methods = useForm<FormValues>({
    resolver: yupResolver(AdjustmentSchema) as any,
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = methods;

  const adjustmentType = watch('type');

  const onSubmit = async (data: FormValues) => {
    if (!product) return;

    try {
      if (data.type === 'decrease' && data.qty > (product.currentStock || 0)) {
        enqueueSnackbar('Quantity cannot exceed available stock', {
          variant: 'error',
        });
        return;
      }

      const service = new StockAdjustmentService();
      const { data: saved } = await service.create({
        productId: product.id,
        ...data,
        qty: Number(data.qty),
      });

      // The server decides: managers/admins apply immediately; other staff
      // need approval above 5 units. Say which happened.
      if (saved?.status === 'Pending') {
        enqueueSnackbar(
          'Adjustment submitted for manager approval. Stock will change once it is approved (Stock → Adjustments).',
          { variant: 'info', autoHideDuration: 8000 },
        );
      } else {
        enqueueSnackbar('Stock adjusted successfully', { variant: 'success' });
      }

      reset();
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error(error);
      enqueueSnackbar(
        Array.isArray(error?.message)
          ? error.message[0]
          : error?.message || 'Failed to adjust stock',
        { variant: 'error' },
      );
    }
  };

  const REASON_OPTIONS = !adjustmentType
    ? []
    : adjustmentType === 'increase'
      ? [
          { value: 'Correction', label: 'Inventory Correction' },
          { value: 'Audit', label: 'Audit Adjustment' },
          { value: 'Other', label: 'Other (Return, Found, etc.)' },
        ]
      : [
          { value: 'Damage', label: 'Damaged' },
          { value: 'Theft', label: 'Theft / Lost' },
          { value: 'WriteOff', label: 'Write Off' },
          { value: 'Correction', label: 'Inventory Correction' },
          { value: 'Other', label: 'Other' },
        ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Quick Stock Adjustment</DialogTitle>

      <DialogContent>
        {product && (
          <Typography
            variant="subtitle1"
            sx={{ mb: 3, color: 'text.secondary' }}
          >
            Adjusting: {product.name} (Current: {product.currentStock})
          </Typography>
        )}

        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3}>
            <RHFSelect
              name="type"
              label="Adjustment Type"
              InputLabelProps={{ shrink: true }}
              native={false}
            >
              <MenuItem value="increase">Increase Stock (+)</MenuItem>
              <MenuItem value="decrease">Decrease Stock (-)</MenuItem>
            </RHFSelect>

            <RHFSelect
              name="reason"
              label="Reason"
              InputLabelProps={{ shrink: true }}
              native={false}
            >
              {REASON_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFSelect>

            <RHFTextField
              name="qty"
              label="Quantity"
              type="number"
              InputLabelProps={{ shrink: true }}
            />

            <RHFTextField
              name="notes"
              label="Notes (Optional)"
              multiline
              rows={3}
            />
          </Stack>
        </FormProvider>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          loading={isSubmitting}
        >
          Confirm Adjustment
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
